import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import InfoTooltip from '@common_components/others/infoTooltip.component';
import ValidationIndicator from '@common_components/others/validationIndicator.component';
import { ChevronUpIcon, dayjs } from '@umanforge/common';

interface InputTimeProps {
  className?: string;
  label?: string;
  tooltip?: string;
  value?: dayjs.Dayjs;
  min: dayjs.Dayjs;
  max: dayjs.Dayjs;
  onChange?: ( value: dayjs.Dayjs ) => void;
  disable?: boolean;
  isRequired?: boolean;
  step?: number;
  labelClassName?: string;
  textSize?: string;
  textable?: boolean;
  inputClassName?: string;
  iconClassName?: string
}

const InputTime: React.FC<InputTimeProps> = ( {
  className = 'w-full',
  label,
  isRequired = false,
  tooltip,
  disable = false,
  value = dayjs(),
  textSize = 'w-3/12 mr-4',
  min,
  max,
  step = 15,
  onChange,
  labelClassName = '',
  textable = false,
  inputClassName = 'min-h-11',
  iconClassName = 'p-1'
} ) => {
  const [ stringValue, setStringValue ] = useState<string>( value.format( 'H' ) + ' : ' + value.format( 'mm' ) );
  const [ error, setError ] = useState<boolean>( false );
  const [ isOpen, setIsOpen ] = useState<boolean>( false );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );

  const ref = useRef<HTMLDivElement>( null );
  const scrollContainerRef = useRef<HTMLDivElement | null>( null );
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>( {} );


  // Use Memo
  const inputValue = useMemo( () => {
    return value;
  }, [ value ] );

  const options: string[] = useMemo( () => {
    const optionList: string[] = [];
    const step_ = step < 15 ? 15 : step;
    let cursor = min;
    if ( max.diff( min ) > 0 ) {
      while ( max.diff( cursor ) >= 0 ) {
        optionList.push( cursor.format( 'HH' ) + ' : ' + cursor.format( 'mm' ) );
        cursor = cursor.add( step_, 'minute' );
      }
    }
    return optionList;
  }, [ min, max, step ] );



  useEffect( () => {
    if ( !isFirstRender ) {
      setStringValue( inputValue.format( 'H' ) + ' : ' + inputValue.format( 'mm' ) );
    }
  }, [ inputValue ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [] );

  useEffect( () => {
    document.addEventListener( 'click', onClickOutside );

    if ( isOpen ) {
      let optionElement = optionRefs.current[ stringValue ];

      // Si la valeur n'est pas dans les options, trouve l'option la plus proche de la valeur
      if ( !optionElement ) {
        const strValue = parseTime( stringValue );
        const closestOption = options.find( option => {
          return parseTime( option ) > strValue;
        } );
        optionElement = optionRefs.current[ closestOption ?? '' ];
      }


      const container = scrollContainerRef.current;

      if ( optionElement && container ) {
        const optionTop = optionElement.offsetTop;
        container.scrollTo( {
          top: optionTop,
          behavior: 'instant',
        } );
      }
    }

    return () => {
      document.removeEventListener( 'click', onClickOutside );
    };
  }, [ isOpen ] );

  const parseTime = ( str: string ) => {
    const [ h, m ] = str.split( ' : ' );
    return Number( h ) * 100 + Number( m );
  };

  const onClickOutside = useCallback( ( event: MouseEvent ) => {
    event.preventDefault();
    if ( isOpen && ( ref.current && !ref.current.contains( event.target as Node ) ) ) {
      setIsOpen( false );
    }
  }, [ isOpen ] );


  const handleValueTexted  = ( strValue: string ) => {
    const [ strHour, strMinute ] = strValue.split( ':' );
    if ( strHour && strMinute ) {
      const hour = Number.parseInt( strHour );
      const minute = Number.parseInt( strMinute );
      if ( !Number.isNaN( hour ) && !Number.isNaN( minute ) ) {
        const newValue = inputValue.set( 'hour', hour ).set( 'minute', minute );
        if ( newValue.diff( max ) <= 0 && newValue.diff( min ) >= 0 ) {
          value = newValue;
          if ( onChange != undefined ) {
            onChange( newValue );
          }
          setStringValue( value.format( 'H' ) + ' : ' + value.format( 'mm' ) );
          setError( false );
          return;
        }
        else {
          setError( true );
        }
      }
    }
    setStringValue( value.format( 'H' ) + ' : ' + value.format( 'mm' ) );
  };



  return (
    <div className={`rounded-md ${className} flex`} >
      {label && (
        <div className={`flex gap-2 relative items-center ${textSize}`}>
          <p className={`text-gray-600 truncate flex items-center ${labelClassName}`}>
            {label}
          </p>
          {tooltip && (
            <InfoTooltip label={label} tooltip={tooltip} />
          )}
        </div>
      )}

      <div className={'relative w-full flex items-center space-x-3'} >
        <div className='relative w-fit' ref={ref}>
          <ValidationIndicator isRequired={isRequired} isValid={ isRequired } />
          <div
            className='absolute ml-20 w-fit h-fit rounded-md hover:bg-gray-100 m-2'
            onClick={ () => { setIsOpen( !isOpen ); } }
            onBlur={ () => {;  }}
          >
            <ChevronUpIcon className={`${ isOpen && 'rotate-180'} ${iconClassName}`} />
          </div>
          { isOpen &&
            <div className='absolute w-full z-[1000] bg-white mt-10 overflow-y-scroll h-48' ref={scrollContainerRef}>
              { options.map( option => {
                return <div
                  key={option}
                  className='hover:bg-gray-100'
                  onClick={ () => {
                    setIsOpen( false );
                    handleValueTexted( option );
                  }}
                  ref={element => {
                    optionRefs.current[ option ] = element;
                  }}
                >
                  <p className='ml-4'>{option}</p>
                </div>;
              } ) }
            </div>
          }
          <input
            name={label}
            disabled={textable}
            value={ stringValue }
            className={`
              p-2 w-28 shadow outline-none rounded-md border-[1px] pl-4
              ${!error && 'hover:border-black focus:border-black' }
              ${disable ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white' } 
              ${error && 'border-red-400 border-2' }
              ${inputClassName}
            `}
            onPaste={e => {
              const pasted = e.clipboardData.getData( 'text' );
              if ( !/^\d+$/.test( pasted ) ) {
                e.preventDefault();
              }
            }}
            onChange={ ev => {
              setStringValue( ev.target.value );
            } }
            onKeyUp={ event => {
              if ( event.key == 'Enter' ) {
                handleValueTexted( stringValue );
              }
            } }
            onBlur={ () => {
              handleValueTexted( stringValue );
            } }
          />
        </div>
      </div>
    </div>
  );
};

export default InputTime;
